import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiExternalLink, FiPackage, FiDownload, FiCalendar, FiGithub } from 'react-icons/fi';
import api from '../utils/api';
import SkillCard, { type SkillData } from '../components/SkillCard';

function sanitizeUrl(url: string): string {
  const trimmed = url.trim().toLowerCase();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('/')) {
    return url;
  }
  return '#';
}

interface UserProfile {
  _id: string;
  username: string;
  displayName?: string;
  avatarUrl?: string;
  bio?: string;
  website?: string;
  githubUrl?: string;
  joinedAt?: string;
  skillCount?: number;
  totalInstalls?: number;
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toString();
}

const PLACEHOLDER_PROFILE: UserProfile = {
  _id: 'demo',
  username: 'strongly-ai',
  displayName: 'Strongly AI',
  bio: 'Building the AI skills ecosystem. Open source tools for the next generation of AI coding agents.',
  website: 'https://strongly.ai',
  githubUrl: 'https://github.com/strongly-ai',
  joinedAt: '2025-01-15T00:00:00Z',
  skillCount: 12,
  totalInstalls: 45230,
};

const PLACEHOLDER_SKILLS: SkillData[] = [
  { _id: '1', name: 'code-review', owner: 'strongly-ai', description: 'Comprehensive code review with security analysis, performance optimization, and best practices.', categories: ['code-quality', 'security'], compatibleAgents: ['claude-code', 'cursor', 'windsurf'], installCount: 12450, stars: 342, authorUsername: 'strongly-ai' },
  { _id: '2', name: 'test-generator', owner: 'strongly-ai', description: 'Auto-generate comprehensive test suites with edge cases, mocking, and coverage optimization.', categories: ['testing', 'automation'], compatibleAgents: ['claude-code', 'cursor', 'windsurf', 'cline'], installCount: 7650, stars: 198, authorUsername: 'strongly-ai' },
  { _id: '3', name: 'docs-writer', owner: 'strongly-ai', description: 'Generate and maintain documentation with API docs, README files, and inline comments.', categories: ['documentation'], compatibleAgents: ['claude-code', 'cursor', 'copilot'], installCount: 6340, stars: 167, authorUsername: 'strongly-ai' },
  { _id: '4', name: 'api-builder', owner: 'strongly-ai', description: 'Build REST and GraphQL APIs with validation, authentication, and OpenAPI documentation.', categories: ['api', 'backend'], compatibleAgents: ['claude-code', 'cursor', 'windsurf', 'devin'], installCount: 4890, stars: 132, authorUsername: 'strongly-ai' },
];

export default function Profile() {
  const { username } = useParams<{ username: string }>();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [skills, setSkills] = useState<SkillData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchProfile() {
      setLoading(true);
      try {
        const [profileRes, skillsRes] = await Promise.all([
          api.get(`/users/${username}`),
          api.get(`/users/${username}/skills`),
        ]);
        setProfile(profileRes.data.user || profileRes.data);
        setSkills(skillsRes.data.skills || []);
      } catch (err: any) {
        // Only use placeholder data in development; in production show null (not found)
        if (import.meta.env.DEV && err.response?.status !== 404) {
          setProfile({ ...PLACEHOLDER_PROFILE, username: username || 'unknown' });
          setSkills(PLACEHOLDER_SKILLS.map(s => ({ ...s, owner: username || 'unknown' })));
        } else {
          setProfile(null);
          setSkills([]);
        }
      } finally {
        setLoading(false);
      }
    }
    fetchProfile();
  }, [username]);

  if (loading) {
    return (
      <div className="pt-24 pb-16 max-w-6xl mx-auto px-4">
        <div className="flex flex-col md:flex-row gap-10">
          <div className="w-full md:w-80 space-y-4">
            <div className="w-32 h-32 shimmer rounded-full mx-auto" />
            <div className="h-6 shimmer rounded-lg w-48 mx-auto" />
            <div className="h-4 shimmer rounded-lg w-64 mx-auto" />
          </div>
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-52 shimmer rounded-2xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="pt-24 pb-16 max-w-6xl mx-auto px-4 text-center">
        <h2 className="text-2xl font-bold text-white mb-4">User Not Found</h2>
        <p className="text-slate-400 mb-6">The user @{username} could not be found.</p>
        <Link to="/browse" className="btn-gradient text-sm px-6 py-3 rounded-xl inline-block">
          <span>Browse Skills</span>
        </Link>
      </div>
    );
  }

  const totalInstalls = profile.totalInstalls || skills.reduce((sum, s) => sum + (s.installCount || 0), 0);
  const joinDate = profile.joinedAt ? new Date(profile.joinedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long' }) : null;

  return (
    <div className="pt-24 pb-16 min-h-screen">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex flex-col md:flex-row gap-10">
          {/* Profile Sidebar */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full md:w-80 shrink-0"
          >
            <div className="glass-card p-8 text-center md:text-left space-y-5 sticky top-24">
              {/* Avatar */}
              <div className="w-28 h-28 rounded-full bg-gradient-to-br from-brand-500 to-brand-purple-500 mx-auto md:mx-0 flex items-center justify-center text-white text-4xl font-bold shadow-glow-mixed">
                {(profile.displayName || profile.username)[0].toUpperCase()}
              </div>

              {/* Name */}
              <div>
                <h1 className="text-2xl font-bold text-white">
                  {profile.displayName || profile.username}
                </h1>
                <p className="text-slate-500 text-sm">@{profile.username}</p>
              </div>

              {/* Bio */}
              {profile.bio && (
                <p className="text-sm text-slate-400 leading-relaxed">{profile.bio}</p>
              )}

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 py-4 border-y border-white/5">
                <div>
                  <div className="text-xl font-bold text-white flex items-center justify-center md:justify-start gap-1.5">
                    <FiPackage size={16} className="text-brand-400" />
                    {skills.length}
                  </div>
                  <div className="text-xs text-slate-500">Skills</div>
                </div>
                <div>
                  <div className="text-xl font-bold text-white flex items-center justify-center md:justify-start gap-1.5">
                    <FiDownload size={16} className="text-brand-purple-400" />
                    {formatCount(totalInstalls)}
                  </div>
                  <div className="text-xs text-slate-500">Installs</div>
                </div>
              </div>

              {/* Links */}
              <div className="space-y-2">
                {profile.website && (
                  <a
                    href={sanitizeUrl(profile.website)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-slate-400 hover:text-brand-400 transition-colors"
                  >
                    <FiExternalLink size={14} />
                    {profile.website.replace(/^https?:\/\//, '')}
                  </a>
                )}
                {profile.githubUrl && (
                  <a
                    href={sanitizeUrl(profile.githubUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-slate-400 hover:text-brand-400 transition-colors"
                  >
                    <FiGithub size={14} />
                    GitHub
                  </a>
                )}
                {joinDate && (
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <FiCalendar size={14} />
                    Joined {joinDate}
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          {/* Skills Grid */}
          <div className="flex-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mb-8"
            >
              <h2 className="text-2xl font-bold text-white">
                Published <span className="gradient-text">Skills</span>
              </h2>
              <p className="text-slate-400 text-sm mt-1">
                {skills.length} skill{skills.length !== 1 ? 's' : ''} published
              </p>
            </motion.div>

            {skills.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {skills.map((skill, idx) => (
                  <SkillCard key={skill._id} skill={skill} index={idx} />
                ))}
              </div>
            ) : (
              <div className="glass-card p-12 text-center">
                <FiPackage size={32} className="text-slate-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">No skills yet</h3>
                <p className="text-slate-500 text-sm">
                  This user hasn&apos;t published any skills yet.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
